<template>
    <div
        class="delete-group-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-group-modal-title"
        aria-describedby="delete-group-modal-description"
        @click.self="closeModal"
    >
        <div
            ref="dialog"
            class="delete-group-modal__dialog"
        >

            <div class="delete-group-modal__body">
                <div
                    class="delete-group-modal__icon"
                    aria-hidden="true"
                >
                    <i class="fa fa-trash"></i>
                </div>

                <p
                    id="delete-group-modal-description"
                    class="delete-group-modal__message"
                >
                    Are you sure you want to delete
                    <strong>{{ formattedGroupName }}</strong>?
                </p>

                <p class="delete-group-modal__description">
                    This action cannot be undone. The group and
                    its conversation will no longer be available.
                </p>

                <p
                    v-if="errorMessage"
                    class="delete-group-modal__error"
                    role="alert"
                >
                    <i
                        class="fa fa-exclamation-circle"
                        aria-hidden="true"
                    ></i>

                    {{ errorMessage }}
                </p>
            </div>

            <footer class="delete-group-modal__footer">
                <button
                    type="button"
                    class="
                        delete-group-modal__button
                        delete-group-modal__button--cancel
                    "
                    :disabled="isDeleting"
                    @click="closeModal"
                >
                    Cancel
                </button>

                <button
                    ref="deleteButton"
                    type="button"
                    class="
                        delete-group-modal__button
                        delete-group-modal__button--delete
                    "
                    :disabled="isDeleting || !hasValidRoomId"
                    @click="deleteGroup"
                >
                    <i
                        v-if="isDeleting"
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <i
                        v-else
                        class="fa fa-trash"
                        aria-hidden="true"
                    ></i>

                    <span>
                        {{
                            isDeleting
                                ? 'Deleting...'
                                : 'Delete Group'
                        }}
                    </span>
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS =
    'body_right_scroll_remove';

export default {
    name: 'DeleteGroupPopup',

    props: {
        groupInfo: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            isDeleting: false,
            errorMessage: '',
            previouslyFocusedElement: null,
            globalListenersAdded: false
        };
    },

    computed: {
        groupName() {
            if (!this.groupInfo) {
                return '';
            }

            const name =
                this.groupInfo.group_name ||
                this.groupInfo.name ||
                this.groupInfo.room_name ||
                '';

            return String(name).trim();
        },

        formattedGroupName() {
            return this.groupName
                ? '"' + this.groupName + '"'
                : 'this group';
        },

        roomId() {
            if (!this.groupInfo) {
                return null;
            }

            if (
                this.groupInfo.chat_room !== undefined &&
                this.groupInfo.chat_room !== null
            ) {
                return this.groupInfo.chat_room;
            }

            if (
                this.groupInfo.room_id !== undefined &&
                this.groupInfo.room_id !== null
            ) {
                return this.groupInfo.room_id;
            }

            if (
                this.groupInfo.id !== undefined &&
                this.groupInfo.id !== null
            ) {
                return this.groupInfo.id;
            }

            return null;
        },

        hasValidRoomId() {
            return (
                this.roomId !== null &&
                this.roomId !== undefined &&
                String(this.roomId).trim() !== ''
            );
        }
    },

    mounted() {
        this.previouslyFocusedElement =
            document.activeElement;

        this.addGlobalListeners();

        this.$nextTick(() => {
            if (this.$refs.deleteButton) {
                this.$refs.deleteButton.focus();
            }
        });
    },

    beforeDestroy() {
        this.removeGlobalListeners();
    },

    methods: {
        async deleteGroup() {
            if (this.isDeleting) {
                return;
            }

            if (!this.hasValidRoomId) {
                this.errorMessage =
                    'Group information is unavailable.';

                return;
            }

            this.isDeleting = true;
            this.errorMessage = '';

            try {
                const response =
                    await this.$store.dispatch(
                        'deleteGroup',
                        {
                            id: this.roomId
                        }
                    );

                /*
                 * Clear the active group and remove its
                 * cached messages after deletion succeeds.
                 */
                bus.$emit(
                    'roomClearedBus',
                    this.roomId
                );

                await this.refreshRoomLists();

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'Group deleted successfully.'
                    )
                );

                this.closeModal(true);
            } catch (error) {
                this.errorMessage =
                    this.getErrorMessage(
                        error,
                        'Unable to delete the group. Please try again.'
                    );

                console.error(
                    'Unable to delete group:',
                    error
                );
            } finally {
                this.isDeleting = false;
            }
        },

        async refreshRoomLists() {
            await Promise.all([
                this.refreshPinnedRooms(),
                this.refreshNormalRooms()
            ]);
        },

        async refreshPinnedRooms() {
            try {
                await this.$store.dispatch(
                    'fetchPinnedChatRooms'
                );
            } catch (error) {
                console.error(
                    'Unable to refresh pinned rooms:',
                    error
                );
            }
        },

        async refreshNormalRooms() {
            try {
                await this.$store.dispatch(
                    'fetchNormalChatRooms'
                );
            } catch (error) {
                console.error(
                    'Unable to refresh normal rooms:',
                    error
                );
            }
        },

        getSuccessMessage(
            response,
            fallbackMessage
        ) {
            if (!response || !response.data) {
                return fallbackMessage;
            }

            const responseData = response.data;

            return (
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                fallbackMessage
            );
        },

        getErrorMessage(
            error,
            fallbackMessage
        ) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (!responseData) {
                return error && error.message
                    ? error.message
                    : fallbackMessage;
            }

            const nestedData =
                responseData.data &&
                typeof responseData.data ===
                    'object'
                    ? responseData.data
                    : null;

            if (
                nestedData &&
                Array.isArray(
                    nestedData.non_field_errors
                ) &&
                nestedData.non_field_errors.length
            ) {
                return nestedData.non_field_errors[0];
            }

            if (
                Array.isArray(
                    responseData.non_field_errors
                ) &&
                responseData.non_field_errors.length
            ) {
                return responseData
                    .non_field_errors[0];
            }

            return (
                responseData.user_msg ||
                responseData.msg ||
                responseData.message ||
                responseData.detail ||
                fallbackMessage
            );
        },

        handleKeydown(event) {
            if (
                event &&
                event.key === 'Escape' &&
                !this.isDeleting
            ) {
                this.closeModal();
            }
        },

        addGlobalListeners() {
            if (this.globalListenersAdded) {
                return;
            }

            document.body.classList.add(
                BODY_SCROLL_CLASS
            );

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );

            this.globalListenersAdded = true;
        },

        removeGlobalListeners() {
            if (!this.globalListenersAdded) {
                return;
            }

            document.body.classList.remove(
                BODY_SCROLL_CLASS
            );

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );

            this.globalListenersAdded = false;
        },

        closeModal(forceClose) {
            if (
                this.isDeleting &&
                forceClose !== true
            ) {
                return;
            }

            this.removeGlobalListeners();

            bus.$emit(
                'deleteGroupInfoPopupBus',
                false
            );

            this.restoreFocus();
        },

        restoreFocus() {
            const previousElement =
                this.previouslyFocusedElement;

            this.$nextTick(() => {
                if (
                    previousElement &&
                    document.body.contains(
                        previousElement
                    ) &&
                    typeof previousElement.focus ===
                        'function'
                ) {
                    previousElement.focus();
                }

                this.previouslyFocusedElement =
                    null;
            });
        }
    }
};
</script>

<style lang="scss" scoped>
$modal-overlay: rgba(0, 0, 0, 0.92);
$modal-background: #000000;
$text-primary: #ffffff;
$text-secondary: #b7b7b7;
$accent-color: #ffd500;
$danger-color: #d93c3c;
$danger-hover: #ff4d4d;
$cancel-background: rgba(255, 255, 255, 0.14);
$error-color: #ff6b6b;
$border-color: rgba(255, 255, 255, 0.14);

.delete-group-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    background-color: $modal-overlay;
    font-family:
        'MyriadProRegular',
        'Lato',
        sans-serif;

    &__dialog {
        width: 100%;
        max-width: 520px;
        margin: auto;
        overflow: hidden;
        border-radius: 6px;
        color: $text-primary;
        background-color: $modal-background;
        box-shadow: 0 0 13px
            rgba(255, 213, 0, 0.6);
    }
    &__body {
        padding: 26px 20px 22px;
        text-align: center;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        margin-bottom: 16px;
        border-radius: 50%;
        color: $danger-color;
        background-color: rgba(
            217,
            60,
            60,
            0.12
        );
        font-size: 28px;
    }

    &__message {
        margin: 0;
        color: $text-primary;
        font-size: 18px;
        line-height: 1.5;
        overflow-wrap: anywhere;

        strong {
            color: #87cefa;
            font-weight: 600;
        }
    }

    &__description {
        margin: 10px 0 0;
        color: $text-secondary;
        font-size: 14px;
        line-height: 1.5;
    }

    &__error {
        margin: 18px 0 0;
        padding: 10px 12px;
        border: 1px solid rgba(
            255,
            107,
            107,
            0.35
        );
        border-radius: 4px;
        color: $error-color;
        background-color: rgba(
            255,
            107,
            107,
            0.08
        );
        font-size: 14px;
        line-height: 1.4;
        text-align: left;

        .fa {
            margin-right: 6px;
        }
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 0 20px 20px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 125px;
        min-height: 40px;
        padding: 9px 16px;
        border: 0;
        border-radius: 4px;
        color: $text-primary;
        font-family: inherit;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        box-shadow: none;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease,
            transform 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:hover:not(:disabled){
            transform: translateY(-1px);
        }

        &:focus {
            outline: 2px solid $accent-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: $cancel-background;

            &:hover:not(:disabled){
                color: #22292f;
                background-color: $accent-color;
            }
        }

        &--delete {
            background-color: $danger-color;

            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                background-color: $danger-hover;
            }
        }
    }
}

@media only screen and (max-width: 480px) {
    .delete-group-modal {
        align-items: flex-end;
        padding: 10px;

        &__header {
            padding: 16px;
        }

        &__title {
            font-size: 18px;
        }

        &__body {
            padding: 22px 16px;
        }

        &__footer {
            flex-direction: column-reverse;
            padding: 0 16px 16px;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>